import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { AbstractControl } from '@angular/forms';
import { Router } from '@angular/router';
import {
  ApiNewDokumentPowiazanyDalDto,
  AttachmentsComponent,
  ColumnHeader,
  disableValidators,
  DocSignService,
  EgibObject,
  FormAlertService,
  MapAction,
  MapExtentUtils,
  MapId,
  MapObject,
  MapObjectApiType,
  MapObjectTableActionType,
  MapObjectTableState,
  MapPortalName,
  MapSettings,
  MapSettingsService,
  MapState,
  MapStateService,
  MapViewActionType,
  markFormGroupsTouched,
  PortalId,
  PzService,
  restoreValidators,
  ShapeUtils,
  StreetService,
  ToolType,
} from '@gk/gk-modules';
import { TranslateService } from '@ngx-translate/core';
import * as _ from 'lodash';
import { ToastrService } from 'ngx-toastr';
import {
  catchError,
  from,
  map,
  Observable,
  of,
  switchMap,
  takeWhile,
  tap,
  throwError,
} from 'rxjs';
import { ProxyDetailsComponent } from '../../../components/proxy-details/proxy-details.component';
import { NewRequestHelperService } from '../../../services/new-request-helper/new-request-helper.service';
import { PlaceService } from '../../../services/place/place.service';
import {
  BsMessageType,
  WorkspaceStateDraftVersion,
  WorkspaceStateId,
} from '../../../services/request-workspace-state/request-workspace-state.model';
import { BaseNewRequestComponent } from '../../../shared/base-new-request/base-new-request.component';
import { DateRangePickerComponent } from '../../../shared/date-range-picker/date-range-picker.component';
import { InvestorDetailsComponent } from '../../../shared/investor-details/investor-details.component';
import { RequestReplyFormComponent } from '../../../shared/request-reply-form/request-reply-form.component';
import {
  DateHelperUtil,
  NgbDateRange,
} from '../../../utils/date-helper/date-helper.util';
import { PLNewRequestControlName } from '../services/new-request-form/new-request-form.model';
import { PLNewRequestFormService } from '../services/new-request-form/new-request-form.service';
import {
  PLNewRequest,
  PLNewRequestDto,
  PLNewRequestFormWorkspaceState,
  PLRequestMode,
} from '../services/new-request/new-request.model';

@Component({
  selector: 'app-new-property-lease-request',
  templateUrl: './new-property-lease-request.component.html',
  styleUrls: ['./new-property-lease-request.component.scss'],
  standalone: false,
})
export class NewPropertyLeaseRequestComponent
  extends BaseNewRequestComponent
  implements OnInit, OnDestroy
{
  override readonly controlName = PLNewRequestControlName;
  readonly mapObjectTableState = new MapObjectTableState(
    [
      new ColumnHeader('mapObjectNumber', 'PARCEL_SEARCH_FORM.PARCEL_NUMBER'),
      new ColumnHeader('districtName', 'PARCEL_SEARCH_FORM.DISTRICT'),
      new ColumnHeader('mapSheet', 'PARCEL_SEARCH_FORM.SHEET'),
      new ColumnHeader('typeName', 'GK.MAP.TYPE', true),
      new ColumnHeader('areaInSquareMeters', 'GK.MAP.AREA_IN_SQUARE_METERS'),
    ],
    undefined,
    this.defaultMapGeometryStyles,
    true,
    true,
    true,
    '25',
  );
  override portalId = PortalId.PropertyLease;
  @ViewChild(InvestorDetailsComponent)
  investorDetailsComponent: InvestorDetailsComponent;
  @ViewChild(ProxyDetailsComponent)
  proxyDetailsComponent: ProxyDetailsComponent;
  @ViewChild(RequestReplyFormComponent)
  requestReplyFormComponent: RequestReplyFormComponent;
  @ViewChild(AttachmentsComponent) attachmentsComponent: AttachmentsComponent;
  @ViewChild('leaseDateRangeComponent')
  leaseDateRangeComponent: DateRangePickerComponent;
  successSigned = false;
  workspaceStateDraftVersion =
    WorkspaceStateDraftVersion.PropertyLeaseRequestFormVersion;
  workspaceStateId = WorkspaceStateId.PropertyLeaseRequestForm;

  constructor(
    private plNewRequestFormService: PLNewRequestFormService,
    public override formAlertService: FormAlertService,
    public override placeService: PlaceService,
    public override streetService: StreetService,
    public override mapStateService: MapStateService,
    public override mapSettingsService: MapSettingsService,
    public override translateService: TranslateService,
    public override toastrService: ToastrService,
    public override docSignService: DocSignService,
    public override newRequestHelperService: NewRequestHelperService,
    public override router: Router,
    public override pzService: PzService,
  ) {
    super(
      pzService,
      newRequestHelperService,
      docSignService,
      router,
      translateService,
      mapSettingsService,
      mapStateService,
      formAlertService,
      placeService,
      streetService,
      undefined,
      toastrService,
    );
  }

  override ngOnInit(): void {
    this.createForm();
    this.fetchApplicantMapSettings();
    this.subscribeToDocSignTranslations();
    this.fetchWrongFileText();
    this.handleRequestModeChange();
  }

  override setMapState(mapSettings: MapSettings): void {
    this.mapState = new MapState(
      MapId.PropertyLease,
      this.mapStateService.getViewState(MapId.PropertyLease, mapSettings),
      this.mapStateService.getToolbarState(
        [ToolType.AnyPolygon, ToolType.LandParcel],
        mapSettings.papers,
        undefined,
        undefined,
        true,
      ),
      this.initialToolsState,
      this.mapStateService.getLayersState(
        MapId.PropertyLease,
        mapSettings,
        MapPortalName.Applicant,
      ),
      [this.mapObjectTableState],
    );
  }

  createForm(): void {
    this.formGroup = this.plNewRequestFormService.getFormGroup();
  }

  override shouldShowRequiredAlert(formControl: AbstractControl): boolean {
    return this.formAlertService.shouldShowErrorAlert(
      formControl,
      'required',
      this.submitted,
    );
  }

  isPurposeFormControlValid(): boolean {
    return this.getPurposeFormControl().valid;
  }

  getPurposeFormControl(): AbstractControl {
    return this.formGroup.get(this.controlName.Purpose);
  }

  isPlaceFormControlValid(): boolean {
    return this.getPlaceFormControl().valid;
  }

  areAreaFormControlsValid(): boolean {
    return this.isAreaFormControlValid() && this.areLandParcelsChosen();
  }

  areLandParcelsChosen(): boolean {
    return this.mapState.mapObjectTablesState[0].mapObjects.some(
      (mapObject) => mapObject.type === MapObjectApiType.LandParcel,
    );
  }

  isAreaFormControlValid(): boolean {
    return this.getAreaFormControl().valid;
  }

  getAreaFormControl(): AbstractControl {
    return this.formGroup.get(this.controlName.AreaToLease);
  }

  isDateRangeInvalid(): boolean {
    return !this.isDateRangeCorrect() || this.isDateRangeEmpty();
  }

  isDateRangeCorrect(): boolean {
    const formControlValue = this.getLeaseDateFormControl().value || {};

    return DateHelperUtil.isToDateNotLessThanFromDate(
      formControlValue.from,
      formControlValue.to,
    );
  }

  isDateRangeEmpty(): boolean {
    const formControlValue = this.getLeaseDateFormControl().value || {};

    return _.isEmpty(formControlValue.from) && _.isEmpty(formControlValue.to);
  }

  updateDateRangeFormControl(dateRange: NgbDateRange): void {
    this.getLeaseDateFormControl().setValue(dateRange);
  }

  getLeaseDateFormControl(): AbstractControl {
    return this.formGroup.get(this.controlName.LeaseDate);
  }

  handleSubmit(): void {
    this.submitted = true;
    markFormGroupsTouched(this.getInvestorDetailsFormGroup());
    if (!this.isRequestValid()) {
      return;
    }

    this.sendRequest();
  }

  isRequestValid(): boolean {
    return (
      this.formGroup.valid &&
      this.attachmentsComponent.areDocumentsValid() &&
      (this.isAnexMode() ||
        (this.areLandParcelsChosen() &&
          !this.isDateRangeInvalid() &&
          this.isAreaFormControlValid()))
    );
  }

  sendRequest(): void {
    this.getRequestBody()
      .pipe(
        tap(() => {
          this.docSignPending = true;
          this.setDocSignMsg(BsMessageType.Info, 'SENDING_REQUEST');
        }),
        switchMap((body) =>
          this.docSignService.addToSign(
            body,
            '/api/interesant/wniosek/wydzierzawienieNieruchomosci/addToSign',
          ),
        ),
        takeWhile(() => this.isAlive),
      )
      .subscribe({
        next: (addedDocToSignResponse) => {
          this.handleSendAndValidateSuccess(addedDocToSignResponse);
        },
        error: () => this.handleSendAndValidateFailure(),
      });
  }

  getRequestBody = (): Observable<PLNewRequestDto> => {
    return from(
      Promise.all(this.attachmentsComponent.getConvertedFiles()).catch(
        (error) => new Error(error),
      ),
    ).pipe(
      switchMap((apiFiles) => {
        const apiFilesArray = Object.values(apiFiles).flatMap((file) => file);
        if (
          (Array.isArray(apiFilesArray) &&
            apiFilesArray.some((file) => file instanceof Error)) ||
          apiFiles instanceof Error
        ) {
          this.errorSubmitMessage = this.wrongFilesErrorText;
          this.toastrService.error(this.wrongFilesErrorText);

          return throwError(() => new Error(this.wrongFilesErrorText));
        }

        return of(apiFiles);
      }),
      map((apiFiles) =>
        PLNewRequest.fromAppToApi(
          this.getFormValue(),
          this.getIssueRanges(),
          this.getLandParcels(),
          apiFiles as ApiNewDokumentPowiazanyDalDto[],
        ),
      ),
      catchError((err) => {
        console.error(err);

        return throwError(() => err);
      }),
    );
  };

  getWorkspaceStateToSave = (): PLNewRequestFormWorkspaceState => ({
    formValue: this.getFormValue(),
    parcels: this.getLandParcels(),
    issueRanges: this.getIssueRanges(),
  });

  handleLoadWorkspaceResponse(state: PLNewRequestFormWorkspaceState): void {
    if (state.formValue?.leaseDate) {
      this.leaseDateRangeComponent.setPeriod(state.formValue.leaseDate);
    }
    this.formGroup.patchValue({
      ...state.formValue,
      investorDetails: {
        ...state.formValue.investorDetails,
        addressSelect: undefined,
      },
    });
    if (state.formValue?.investorDetails?.addressSelect) {
      this.investorDetailsComponent.setAddressSelectFormGroupValue(
        state.formValue.investorDetails.addressSelect,
      );
    }
    if (state.formValue?.proxyDetailsArray) {
      this.proxyDetailsComponent.setFormGroupValue(
        state.formValue.proxyDetailsArray,
      );
    }
    this.requestReplyFormComponent.patchRequestReplyFormControls({
      documentationForm: state.formValue.documentationForm,
      shippingForm: state.formValue.shippingForm,
    });
    if (state.parcels) {
      this.handleMapAction(
        new MapAction(MapObjectTableActionType.AddNew, [...state.parcels]),
      );
    }
    if (state.issueRanges) {
      const mapObjects = state.issueRanges.map((workRange) =>
        ShapeUtils.getAnyGeometryMapObject(
          workRange,
          MapObjectApiType.ExtentOrPolygon,
        ),
      );
      this.handleMapAction(
        new MapAction(MapObjectTableActionType.AddToExisting, mapObjects),
      );
      this.handleMapAction(
        new MapAction(
          MapViewActionType.ExtentToFitToChange,
          MapExtentUtils.getMapExtentFromMapObjects(mapObjects),
        ),
      );
      this.handleMapAction(
        new MapAction(MapObjectTableActionType.SelectRange, mapObjects),
      );
    }
  }

  getFormValue(): PLNewRequest {
    return this.formGroup.getRawValue();
  }

  getIssueRanges(): string[] {
    return [...this.mapState.mapObjectTablesState[0].mapObjects]
      .filter(
        (mapObject) => mapObject.type === MapObjectApiType.ExtentOrPolygon,
      )
      .map((issueRange) => issueRange.geom);
  }

  getLandParcels(): EgibObject[] {
    return [...this.mapState.mapObjectTablesState[0].mapObjects].filter(
      <(mapObject: MapObject) => mapObject is EgibObject>(
        ((mapObject) => mapObject.type === MapObjectApiType.LandParcel)
      ),
    );
  }

  override handleDocSignSuccess(): void {
    this.successSigned = true;
    this.docSignUrl = '';
    this.router.navigateByUrl('/property-lease/requests-list');
  }

  isAnexMode(): boolean {
    return this.getRequestModeValue() === PLRequestMode.Annex;
  }

  isNewMode(): boolean {
    return this.getRequestModeValue() === PLRequestMode.New;
  }

  isAnexDateFromFormControlValid(): boolean {
    return this.getAnexDateFromFormControl().valid;
  }

  getAnexDateFromFormControl(): AbstractControl {
    return this.formGroup.get(this.controlName.AnexDateFrom);
  }

  isAnexDescriptionFormControlValid(): boolean {
    return this.getAnexDescriptionFormControl().valid;
  }

  getAnexDescriptionFormControl(): AbstractControl {
    return this.formGroup.get(this.controlName.AnexDescription);
  }

  isLeaseNumberFormControlValid(): boolean {
    return this.getLeaseNumberFormControl().valid;
  }

  getLeaseNumberFormControl(): AbstractControl {
    return this.formGroup.get(this.controlName.LeaseNumber);
  }

  override onRequestModeChange(requestMode: PLRequestMode): void {
    const anexModeFormControls = this.getAnexModeFormControls();
    const newModeFormControls = this.getNewModeFormControls();
    switch (requestMode) {
      case PLRequestMode.New: {
        disableValidators(anexModeFormControls);
        restoreValidators(newModeFormControls);

        break;
      }
      case PLRequestMode.Annex: {
        disableValidators(newModeFormControls);
        restoreValidators(anexModeFormControls);

        break;
      }
      default: {
        console.warn(`Unknown request mode: ${requestMode}`);
      }
    }
  }

  getAnexModeFormControls(): AbstractControl[] {
    return [
      this.getAnexDateFromFormControl(),
      this.getAnexDescriptionFormControl(),
      this.getLeaseNumberFormControl(),
    ];
  }

  getNewModeFormControls(): AbstractControl[] {
    return [
      this.getLeaseDateFormControl(),
      this.getPurposeFormControl(),
      this.getAreaFormControl(),
      this.getPlaceFormControl(),
      this.getLeaseDateFormControl(),
    ];
  }

  ngOnDestroy(): void {
    this.isAlive = false;
  }
}
